import { add, multiplyByRatio, subtract, sum, zero, type Money } from "@stb/core/money"

/** Australian GST: 10% of the GST-exclusive value, i.e. 1/11 of a GST-inclusive price. */
export const GST_RATE = Object.freeze({ numerator: 1, denominator: 10 })

/**
 * - `GST`: taxable supply at 10%
 * - `GST_FREE`: GST-free supply (e.g. most basic food, some health and education)
 * - `INPUT_TAXED`: input-taxed supply (e.g. financial supplies, residential rent)
 * - `BAS_EXCLUDED`: outside the GST system and not reported on the BAS (e.g. drawings, wages)
 */
export type AuTaxCode = "GST" | "GST_FREE" | "INPUT_TAXED" | "BAS_EXCLUDED"

/** Whether entered amounts already include GST. */
export type AmountsAre = "inclusive" | "exclusive"

export interface GstBreakdown {
  net: Money
  gst: Money
  gross: Money
}

export interface GstLineInput {
  amount: Money
  taxCode: AuTaxCode
  amountsAre: AmountsAre
}

function assertAud(amount: Money): void {
  if (amount.currency !== "AUD") {
    throw new RangeError(`Australian GST applies to AUD amounts, got ${amount.currency}`)
  }
}

/** GST contained in, or to be added to, `amount`, rounded to the nearest cent (halves away from zero). */
function gstOn(amount: Money, amountsAre: AmountsAre): Money {
  const { numerator, denominator } = GST_RATE
  return amountsAre === "inclusive"
    ? multiplyByRatio(amount, numerator, numerator + denominator)
    : multiplyByRatio(amount, numerator, denominator)
}

function breakdown(amount: Money, gst: Money, amountsAre: AmountsAre): GstBreakdown {
  return amountsAre === "inclusive"
    ? { net: subtract(amount, gst), gst, gross: amount }
    : { net: amount, gst, gross: add(amount, gst) }
}

export function calculateLineGst({ amount, taxCode, amountsAre }: GstLineInput): GstBreakdown {
  assertAud(amount)
  const gst = taxCode === "GST" ? gstOn(amount, amountsAre) : zero("AUD")
  return breakdown(amount, gst, amountsAre)
}

export interface DocumentGstOptions {
  amountsAre: AmountsAre
  /**
   * `per-line` (default) rounds GST on each line; `per-document` rounds once on
   * the total of taxable lines. Pick one per document and apply it consistently.
   */
  rounding?: "per-line" | "per-document"
}

export interface DocumentGstResult {
  /** Per-line breakdown, always rounded per line. */
  lines: GstBreakdown[]
  /** Document totals under the chosen rounding method. */
  totals: GstBreakdown
  /** Document GST minus the sum of line GST; zero for per-line rounding. */
  roundingAdjustment: Money
}

function sumBreakdowns(items: readonly GstBreakdown[]): GstBreakdown {
  return {
    net: sum(
      items.map((item) => item.net),
      "AUD",
    ),
    gst: sum(
      items.map((item) => item.gst),
      "AUD",
    ),
    gross: sum(
      items.map((item) => item.gross),
      "AUD",
    ),
  }
}

export function calculateDocumentGst(
  lines: ReadonlyArray<Omit<GstLineInput, "amountsAre">>,
  { amountsAre, rounding = "per-line" }: DocumentGstOptions,
): DocumentGstResult {
  const lineResults = lines.map((line) => calculateLineGst({ ...line, amountsAre }))
  const perLineTotals = sumBreakdowns(lineResults)

  if (rounding === "per-line") {
    return { lines: lineResults, totals: perLineTotals, roundingAdjustment: zero("AUD") }
  }

  const taxableTotal = sum(
    lines.filter((line) => line.taxCode === "GST").map((line) => line.amount),
    "AUD",
  )
  const documentGst = gstOn(taxableTotal, amountsAre)
  const amountTotal = sum(
    lines.map((line) => line.amount),
    "AUD",
  )

  return {
    lines: lineResults,
    totals: breakdown(amountTotal, documentGst, amountsAre),
    roundingAdjustment: subtract(documentGst, perLineTotals.gst),
  }
}
